import { Dialog, DialogTitle, DialogContent, Box, FormControl, InputLabel, Select, CircularProgress, TextField, Typography, Grid, Button, Chip, DialogActions, MenuItem, FormHelperText, Divider, Alert } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { format } from 'date-fns';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ClinicDTO } from '../../../api/ClientDTO';
import { SamplingScheduleOptionDTO } from '../../../api/Scheduler/SchedulerClient';
import { LocalizationProvider, DatePicker } from '@mui/lab';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import { PatientCalendarClient } from '../../../api/PatientCalendar/PatientCalendarClient';
import { REACT_APP_PORTAL_API_URL } from '../../../KeyVariables';
import { PortalScheduleSamplingDTO } from '../../../api/PatientCalendar/PatientCalendarClientDTO';

interface ISamplingScheduleDialogProps {
  medicalAnalysisBatchId?: number | null;
  isOpen: boolean;
  onSave: () => void;
  onClose: () => void;
  shoppingCartIdentity:string;
}

const SamplingScheduleDialog: React.FC<ISamplingScheduleDialogProps> = ({
  isOpen,
  medicalAnalysisBatchId,
  onClose,
  onSave,
  shoppingCartIdentity
}) => {
  const { t } = useTranslation('Portal');

  const [clinics, setClinics] = useState<ClinicDTO[]>([]);
  const [clinicId, setClinicId] = useState<number | ''>('');
  const [date, setDate] = useState<Date | null>(null);
  const [blocks, setBlocks] = useState<SamplingScheduleOptionDTO[]>([]);
  const [selected, setSelected] = useState<SamplingScheduleOptionDTO | null>(null);
  const [loadingClinics, setLoadingClinics] = useState(false);
  const [loadingBlocks, setLoadingBlocks] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveSuccessDate, setSaveSuccessDate] = useState<Date | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const client = new PatientCalendarClient(REACT_APP_PORTAL_API_URL);

  // Load clinics on open
  useEffect(() => {
    if (!isOpen) return;
    setLoadingClinics(true);
    setSaveSuccessDate(null);
    setSaveError(null);
    client.getclinics()
      .then(setClinics)
      .catch(err => console.error(err))
      .finally(() => setLoadingClinics(false));
  }, [isOpen]);

  // Load time slots when clinic and date selected
  useEffect(() => {
    if (!clinicId || !date) return;
    setLoadingBlocks(true);
    setBlocks([]);
    setSelected(null);
    client.getsamplingofficeslots(clinicId as number, new Date(date.getFullYear(), date.getMonth(), date.getDate()))
      .then(options => setBlocks(options.filter(o => o.startDate)))
      .catch(err => console.error(err))
      .finally(() => setLoadingBlocks(false));
  }, [clinicId, date]);

  const clinicOptions = useMemo(
    () => clinics.map(c => (
      <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
    )),
    [clinics]
  );

  const handleClose = () => {
    setClinicId(''); setDate(null); setBlocks([]); setSelected(null);
    setSaveSuccessDate(null); setSaveError(null);
    onClose();
  };

  const handleSave = async () => {
    setSaving(true);
    setLoadingBlocks(true);
    setSaveSuccessDate(null);
    setSaveError(null);
    try {
      const dto = new PortalScheduleSamplingDTO();
      dto.clinicId = Number(clinicId);
      dto.medicalAnalysisBatchId = medicalAnalysisBatchId ?? undefined;
      dto.officeId = selected?.office?.id;
      dto.samplingOffice = selected?.samplingOffice?.id;
      dto.dateStart = selected?.startDate;
      dto.dateEnd = selected?.endDate;
      dto.shoppingCartIdentity = shoppingCartIdentity;

      const ok = await client.schedulesampling(dto);
      if (ok && dto.dateStart) {
        setSaveSuccessDate(dto.dateStart);
        onSave();
      } else {
        setSaveError(t('Portal_SaveError'));
      }
    } catch (e) {
      console.error(e);
      setSaveError(t('Portal_SaveError'));
    } finally {
      setLoadingBlocks(false);
      setSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('Portal_Program')}</DialogTitle>
      <Divider />
      <DialogContent>
        {/* Success or Error Message */}
        {saveSuccessDate && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {t('Portal_SavedOn')} <strong>{format(saveSuccessDate, 'dd.MM.yyyy HH:mm')}</strong>
          </Alert>
        )}
        {saveError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {saveError}
          </Alert>
        )}

        {/* Informative warning */}
        <Box display="flex" alignItems="center" mb={2}>
          <WarningAmberIcon color="warning" />
          <Typography variant="body2" color="warning.main" ml={1}>
            {t('Portal_ScheduleSamplingWarning')}
          </Typography>
        </Box>

        {/* Clinic selection */}
        <FormControl fullWidth margin="normal" disabled={loadingClinics || saving}>
          <InputLabel>{t('Portal_Clinic')}</InputLabel>
          <Select
            value={clinicId}
            label={t('Portal_Clinic')}
            onChange={e => setClinicId(e.target.value as number)}
          >
            {clinicOptions}
          </Select>
          {loadingClinics && <FormHelperText>{t('...')}</FormHelperText>}
        </FormControl>

        {/* Date picker */}
        {clinicId && (
          <FormControl fullWidth margin="normal" disabled={saving}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label={t('Portal_date')}
                value={date}
                onChange={setDate}
                renderInput={params => <TextField {...params} fullWidth />}
              />
            </LocalizationProvider>
          </FormControl>
        )}

        {/* Time slots */}
        {loadingBlocks && (
          <Box display="flex" justifyContent="center" my={2}>
            <CircularProgress />
          </Box>
        )}

        {blocks.length > 0 && (
          <Box my={2}>
            <Typography variant="subtitle1" gutterBottom>{t('Portal_Time')}</Typography>
            <Grid container spacing={1} sx={{ maxHeight: 200, overflowY: 'auto' }}>
              {blocks.map((block,key) => (
                <Grid item xs={4} sm={3} md={2} key={key}>
                  <Button
                    fullWidth size="small"
                    variant={selected === block ? 'contained' : 'outlined'}
                    onClick={() => setSelected(block)}
                    disabled={saving}
                  >
                    {format(new Date(block.startDate!), 'HH:mm')}
                  </Button>
                </Grid>
              ))}
            </Grid>
            {selected && (
              <Box mt={2} display="flex" alignItems="center">
                <Typography variant="body2">{t('Portal_Selected')}:</Typography>
                <Chip label={format(new Date(selected.startDate!), 'HH:mm')} sx={{ ml: 1 }} />
              </Box>
            )}
          </Box>
        )}

        {date && !loadingBlocks && blocks.length === 0 && (
          <Typography color="textSecondary" align="center" my={2}>
            {t('Portal_NoSlots')}
          </Typography>
        )}
      </DialogContent>
      <Divider />
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>{t('Portal_Cancel')}</Button>
        <Button variant="contained" onClick={handleSave} disabled={!selected || saving}>
          {saving ? <CircularProgress size={20} /> : t('Portal_confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SamplingScheduleDialog;
